<template>
  <div class="w-full bg-white rounded-md grow table-container">
    <RecordCard
      v-model:visible="cardVisible"
      :data="cardData"
      :meta="props.meta"
      v-if="cardData"
    >
    </RecordCard>
    <PrimeDataTable
      tableClass="w-full"
      :value="props.records"
      :totalRecords="props.total"
      v-model:first="first"
      paginator
      showGridlines
      stripedRows
      removableSort
      scrollable
      scrollHeight="flex"
      @page="($event) => emits('page', $event)"
      @sort="($event) => emits('sort', $event)"
      @filter="
        ($event) => {
          emits('filter', $event);
        }
      "
      :select-all="selectAll"
      @select-all-change="
        (event: DataTableSelectAllChangeEvent) => {
          // console.log(selectedRows);
          // event.checked
          selectAll = event.checked;
        }
      "
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      :filter="props.filterMode ? true : false"
      size="small"
      :filterDisplay="props.filterMode"
      :loading="props.loading"
      :rowsPerPageOptions="rowsPerPageOptions"
      v-model:rows="pageSize"
      lazy
      :globalFilterFields="filterFields(props.meta?.enabledFilterColumns ?? [])"
      v-model:filters="dataTableFilters"
      v-model:selection="selectedRows"
      :pt="{
        sortIcon: 'pi pi-sort-up-fill',
        tableContainer: {
          class: 'border border-gray-200',
        },
      }"
    >
      <template #empty
        ><span class="text-[14px]"> No records found</span>
      </template>
      <template #paginatorstart>
        <slot name="paginatorstart">
          <PrimeButton
            label="Refresh"
            icon="pi pi-refresh"
            @click="emits('refresh')"
            :loading="props.loading"
          />
        </slot>
      </template>
      <template #paginatorend>
        <slot name="paginatorend" :exportData="exportData" :total="props.total">
          <div class="flex flex-row gap-2">
            <PrimeButton
              label="Export"
              @click="exportData('all')"
              v-if="props.showExport"
            />
          </div>
        </slot>
      </template>
      <slot name="beforecolumns"> </slot>
      <PrimeColumn
        v-if="props.selectionMode != null"
        :selectionMode="props.selectionMode"
        headerStyle="width: 3rem;"
      />
      <PrimeColumn
        field="actions"
        class="w-12"
        v-if="props.showActions && shouldShowActions"
        frozen
        :pt="{
          bodyCell: {
            class: 'bg-neutral-100/50 backdrop-blur-sm ',
          },
          headerCell: {},
        }"
      >
        <template #header>
          <span class="text-[14px] flex justify-center mx-auto w-12"
            >Actions</span
          >
        </template>
        <template #body="{ data: rowData }">
          <RecordActionMenu
            :data="rowData"
            :table="props.table"
            :schema="props.schema"
            :meta="props.meta"
            @delete="
              ($event) => {
                if (meta.data.Table.ShowDelete) {
                  emits('delete', { record: $event.record });
                }
              }
            "
            @show-card="
              () => {
                showCard(rowData);
              }
            "
            @edit="
              ($event) => {
                if (meta.data.Table.ShowEdit) {
                  emits('edit', { record: $event.record });
                }
              }
            "
            @copy="
              ($event) => {
                emits('copy', {
                  record: rowData,
                  copyFields: $event.copyFields,
                });
              }
            "
            @refresh="handleComplete"
          />
        </template>
      </PrimeColumn>
      <PrimeColumn
        v-for="col in props.meta.enabledGridColumns"
        :key="col.Id"
        :field="col.ColumnNamePc"
        :sortable="props.sortable"
        :showFilterMatchModes="false"
        :style="{
          'max-width': col.GridColumnWidth ?? 'auto',
        }"
        :pt="{
          sortIcon: {
            class: 'pi pi-sort-up-fill',
          },
        }"
        showClearButton
      >
        <template #header>
          <span class="text-[14px]">{{ col.ColumnLabel }}</span>
        </template>
        <template #body="{ data }">
          <RecordGridItem :data="data" :col="col" :meta="props.meta" />
        </template>
        <template
          #filter="{ filterModel, filterCallback }"
          v-if="col.ShowInFilter"
        >
          <RecordGridFilter
            :col="col"
            :filterData="dataTableFilters"
            :meta="props.meta"
            v-model:value="filterModel.value"
            @filter="
              (value) => {
                filterCallback();
              }
            "
          />
        </template>
      </PrimeColumn>
      <!-- <PrimeColumn
        field="metaactions"
        v-if="meta.data.Actions && meta.data.Actions.length > 0"
        class="w-40"
      >
        <template #body="{ data }">
          <div class="flex flex-row items-center"></div>
        </template>
      </PrimeColumn> -->
    </PrimeDataTable>
  </div>
</template>

<script setup lang="ts">
import { FilterMatchMode } from "@primevue/core/api";
import type {
  DataTableFilterEvent,
  DataTablePageEvent,
  DataTableSelectAllChangeEvent,
  DataTableSortEvent,
} from "primevue/datatable";
import type { IMetaColumn, IMetaComputed } from "~/scad-shared/types/scad";

const confirm = useConfirm();

const props = withDefaults(
  defineProps<{
    records: any[];
    meta: IMetaComputed;
    metaComposable?: MetaComposable;
    total?: number;
    loading?: boolean;
    filterMode?: "row" | "menu" | undefined;
    sortable?: boolean;
    showActions?: boolean;
    showExport?: boolean;
    schema?: string;
    table: string;
    selectionMode?: "single" | "multiple" | null;
  }>(),
  {
    loading: false,
    filterMode: undefined,
    sortable: true,
    showActions: true,
    showExport: false,
    schema: "public",
    selectionMode: null,
  },
);

const tableMeta =
  props.metaComposable ?? (await useMetaAsync(props.table, props.schema));
const renderless = useRecord(props.table, props.schema, tableMeta);

const selectedRows = defineModel<any>("selectedRows", {
  default: [],
});

const pageSize = defineModel<number>("pageSize", {
  default: 20,
});

const first = defineModel<number>("first", {
  default: 0,
});

const dataTableFilters = defineModel<Indexable>("dataTableFilters", {
  default: () => ({}),
});

const queryFilter = defineModel<Indexable>("queryFilter", {
  default: () => ({}),
});

props.meta.enabledFilterColumns.forEach((col: any) => {
  if (col.SearchType) {
    switch (col.SearchType.toLowerCase()) {
      case "startswith":
        dataTableFilters.value[col.ColumnNamePc] = {
          value: null,
          matchMode: FilterMatchMode.STARTS_WITH,
        };
        break;
      case "endswith":
        dataTableFilters.value[col.ColumnNamePc] = {
          value: null,
          matchMode: FilterMatchMode.ENDS_WITH,
        };
        break;
      default:
        dataTableFilters.value[col.ColumnNamePc] = {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        };
        break;
    }
  } else {
    dataTableFilters.value[col.ColumnNamePc] = {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    };
  }
});

const emits = defineEmits<{
  sort: [event: DataTableSortEvent];
  page: [event: DataTablePageEvent];
  filter: [event: DataTableFilterEvent];
  edit: [event: { record: any }];
  delete: [event: { record: any }];
  copy: [event: { record: any; copyFields: any[] }];
  refresh: [event: void];
}>();

const rowsPerPageOptions = computed(() => {
  const pageOptions = [10, 20, 50, 100];
  if (
    props.meta &&
    !pageOptions.includes(props.meta.data.Table.RecordsPerPage)
  ) {
    pageOptions.push(props.meta.data.Table.RecordsPerPage);
  }

  return pageOptions.sort((a, b) => a - b);
});

function filterFields(columns: IMetaColumn[]) {
  const fields: any[] = [];

  columns.forEach((col) => {
    if (col.ShowInFilter) {
      fields.push(col.ColumnNamePc);
    }
  });

  return fields;
}

async function exportData(type: "create" | "all" | "grid" = "grid") {
  let url = `/scad/scadfw/records/${props.schema}/${props.table}`;
  if (props.meta.data.Table.ReadRecordsRoute) {
    url = `/scad/${props.meta.data.Table.ReadRecordsRoute}`;
  }
  const searchParams = new URLSearchParams();
  if (props.meta.data.Table.ReadRecordsRoute) {
    searchParams.append("format", "csv");
  } else {
    searchParams.append("format", "xlsx");
  }
  // if (type === "create") {
  //   searchParams.append("TableExportColumnType", "create");
  //   searchParams.append("Take", "-1");
  // }
  // if (type === "all") {
  //   searchParams.append("TableExportColumnType", "all");
  //   searchParams.append("Take", "-1");
  // }
  for (const key in queryFilter.value) {
    if (queryFilter.value[key]) {
      searchParams.append(key, queryFilter.value[key]);
    }
  }
  if (props.selectionMode == "multiple" && selectedRows.value.length > 0) {
    const ids = selectedRows.value.map((row: any) => row.Id);
    searchParams.append("Ids", ids.join(","));
  }
  try {
    const response = await fetch(`${url}?${searchParams.toString()}`, {
      method: "GET",
    });

    if (response.status !== 200) {
      toast.add({
        summary: "Error!",
        detail: "Could not export data",
        severity: "error",
        life: 7000,
      });
      return;
    }

    const contentDisposition = response.headers.get("Content-Disposition");
    const filename = contentDisposition
      ? contentDisposition.split("filename=")[1]?.replace(/["']/g, "") ||
        "download"
      : "download";
    // Create download from blob
    response.blob().then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  } catch (error) {
    toast.add({
      summary: "Error!",
      detail: "Could not export data",
      severity: "error",
      life: 7000,
    });
  }
}
const toast = useToast();
function handleComplete(response: any) {
  emits("refresh");
  console.log(response, "response");
  if (response) {
    if (response.action == "error") {
      toast.add({
        summary: "Error!",
        detail: response.error.data.ResponseStatus.Message,
        severity: "error",
        life: 7000,
      });
    }
    if (response.action == "toast") {
      toast.add({
        summary: "Success!",
        detail: response.message,
        severity: "success",
        life: 7000,
      });
    }
  }
  menu.value?.hide();
}

const menu = ref<any>(null);
function resetFilters() {
  props.meta.enabledFilterColumns.forEach((col) => {
    if (col.SearchType) {
      switch (col.SearchType.toLowerCase()) {
        case "startswith":
          dataTableFilters.value[col.ColumnNamePc] = {
            value: null,
            matchMode: FilterMatchMode.STARTS_WITH,
          };
          break;
        case "endswith":
          dataTableFilters.value[col.ColumnNamePc] = {
            value: null,
            matchMode: FilterMatchMode.ENDS_WITH,
          };
          break;
        default:
          dataTableFilters.value[col.ColumnNamePc] = {
            value: null,
            matchMode: FilterMatchMode.CONTAINS,
          };
          break;
      }
    } else {
      dataTableFilters.value[col.ColumnNamePc] = {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      };
    }
  });
}

resetFilters();

const shouldShowActions = computed(() => {
  const { Actions, Table } = props.meta.data;

  const executableActions = Actions.filter(
    (action: { CanExecute: boolean }) => action.CanExecute,
  );
  return (
    props.meta.data.Table.ShowEdit ||
    props.meta.data.Table.ShowDelete ||
    executableActions.length > 0
  );
});

const selectAll = defineModel<boolean>("selectAll", {
  default: false,
});

// Card logic
const cardData = ref<Indexable | null>(null);
const cardVisible = ref(false);

async function showCard(record: any) {
  cardData.value = record;
  cardVisible.value = true;
}

defineExpose({
  resetFilters,
});
</script>

<style scoped>
.table-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-container :deep(.p-datatable) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.table-container :deep(.p-datatable-wrapper) {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.table-container :deep(.p-datatable-scrollable-wrapper) {
  flex: 1;
  min-height: 200px; /* Minimum space for a few rows */
}

.table-container :deep(.p-paginator) {
  flex-shrink: 0;
  margin-top: auto;
}
</style>
